import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { get } from '../services/apiService';
import logger from '../utils/logger';
import { isPastUtcExpiry } from '../utils/dateUtils';
import { getFullUrl } from '../config/apiConfig';
import { 
  ArrowLeft, 
  CreditCard, 
  User, 
  Mail, 
  Phone, 
  MapPin, 
  GraduationCap, 
  Crown, 
  Star, 
  Zap,
  CheckCircle
} from 'lucide-react';

// Environment-based Stripe payment URLs
const getStripeUrls = () => {
  // Use environment variables (REQUIRED - no fallbacks)
  const basicUrl = process.env.REACT_APP_STRIPE_BASIC_URL;
  const standardUrl = process.env.REACT_APP_STRIPE_STANDARD_URL;
  const premiumUrl = process.env.REACT_APP_STRIPE_PREMIUM_URL;

  // Validate that environment variables are set
  if (!basicUrl || !standardUrl || !premiumUrl) {
    console.error('ERROR: Stripe URL environment variables are required. Set REACT_APP_STRIPE_BASIC_URL, REACT_APP_STRIPE_STANDARD_URL, and REACT_APP_STRIPE_PREMIUM_URL');
    return null;
  }

  return {
    PKG_BASIC: basicUrl,
    PKG_STANDARD: standardUrl,
    PKG_PREMIUM: premiumUrl
  };
};

const Purchase = () => {
  // const navigate = useNavigate();
  const location = useLocation();
  const { selectedPackage } = location.state || {};

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [purchaseHistory, setPurchaseHistory] = useState([]);
  const [alreadyPurchased, setAlreadyPurchased] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    grade: '',
    packageId: selectedPackage?.id || '',
    packageName: selectedPackage?.name || '',
    price: selectedPackage?.price || 0,
    // User details
    name: '',
    email: '',
    mobile: '',
    address: '',
    // Card details
  });

  // Load user details and purchase history from localStorage
  useEffect(() => {
    const loadUserDetails = () => {
      try {
        // Use new userInfo from localStorage (no tokens stored)
        const userInfo = localStorage.getItem('userInfo');
        if (userInfo) {
          const user = JSON.parse(userInfo);

          setUserDetails({
            userId: user.id,
            email: user.email,
            name: user.name
          });

          // Pre-fill form with user details
          setFormData(prev => ({
            ...prev,
            name: user.name || '',
            email: user.email || '',
            grade: prev.grade || ''
          }));
        }
      } catch (err) {
        logger.error('Error loading user details:', err);
      }
    };

    loadUserDetails();
  }, []);

  // Fetch purchase history to check for already purchased packages
  useEffect(() => {
    const fetchPurchaseHistory = async () => {
      try {
        const response = await get(getFullUrl('purchases'));
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        
        // Parse the response body
        let actualData = data;
        if (data && data.body) {
          actualData = typeof data.body === 'string' ? JSON.parse(data.body) : data.body;
        }
        
        let purchases = [];
        if (Array.isArray(actualData)) {
          purchases = actualData;
        } else if (actualData && actualData.purchases && Array.isArray(actualData.purchases)) {
          purchases = actualData.purchases;
        }
        
        setPurchaseHistory(purchases);

        // Check if selected package is already purchased and not expired
        if (selectedPackage && purchases.length > 0) {
          const selectedPackageId = selectedPackage.id;
          const selectedGrade = formData.grade;

          // Helper function to check if purchase is expired
          const isPurchaseExpired = (purchase) => isPastUtcExpiry(purchase.purchaseDate);

          // Check if user has already purchased this specific package for this grade and it's not expired
          const isPurchased = purchases.some(purchase => {
            const purchasePackageId = purchase.packageId || purchase.package;
            const purchaseGrade = purchase.grade;

            // Check for exact package-grade match and not expired
            return (
              purchasePackageId === selectedPackageId &&
              purchaseGrade === selectedGrade &&
              !isPurchaseExpired(purchase)
            );
          });

          setAlreadyPurchased(isPurchased);

          if (isPurchased) {
            logger.log(`Package ${selectedPackageId} for grade ${selectedGrade} already purchased and valid`);
          }
        }
        
      } catch (err) {
        logger.error('Error fetching purchase history:', err);
      }
    };

    if (selectedPackage) {
      fetchPurchaseHistory();
    }
  }, [selectedPackage]);

  // Re-check purchase validation when grade changes
  useEffect(() => {
    if (purchaseHistory.length > 0 && selectedPackage && formData.grade) {
      const selectedPackageId = selectedPackage.id;
      const selectedGrade = formData.grade;

      // Helper function to check if purchase is expired
      const isPurchaseExpired = (purchase) => isPastUtcExpiry(purchase.purchaseDate);

      // Check if user has already purchased this specific package for this grade and it's not expired
      const isPurchased = purchaseHistory.some(purchase => {
        const purchasePackageId = purchase.packageId || purchase.package;
        const purchaseGrade = purchase.grade;

        // Check for exact package-grade match and not expired
        return (
          purchasePackageId === selectedPackageId &&
          purchaseGrade === selectedGrade &&
          !isPurchaseExpired(purchase)
        );
      });

      setAlreadyPurchased(isPurchased);

      if (isPurchased) {
        logger.log(`Package ${selectedPackageId} for grade ${selectedGrade} already purchased and valid`);
      }
    }
  }, [formData.grade, purchaseHistory, selectedPackage]);

  // Check for Stripe success redirect
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const paymentStatus = urlParams.get('payment');
    
    if (paymentStatus === 'success') {
      // Handle successful payment
    }
  }, []);

  // Validate Australian mobile number
  const isValidAustralianMobile = (number) => {
    // Remove spaces and +61 country code prefix if present
    const cleaned = number.replace(/\s/g, '').replace(/^\+61/, '');
    
    // Australian mobile numbers: starts with 04, 10 digits total
    const auMobileRegex = /^04\d{8}$/;
    return auMobileRegex.test(cleaned);
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Clear field error when user starts typing
    if (fieldErrors[name]) {
      setFieldErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
    
    // For mobile number, only allow digits, spaces, and + at start
    if (name === 'mobile') {
      // Remove any non-digit, non-space, non-+ characters
      let cleanedValue = value.replace(/[^\d\s+]/g, '');
      
      // Ensure + only appears at the start
      if (cleanedValue.includes('+')) {
        cleanedValue = cleanedValue.replace(/\+/g, '');
        if (value.trim().startsWith('+')) {
          cleanedValue = '+' + cleanedValue;
        }
      }
      
      // Limit to reasonable length (max 14: +61 412 345 678 or 0412 345 678)
      cleanedValue = cleanedValue.substring(0, 14);
      
      setFormData(prev => ({
        ...prev,
        [name]: cleanedValue
      }));
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  

  // Handle purchase submission
  const handlePurchase = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Check if package is already purchased
      if (alreadyPurchased) {
        throw new Error(`You have already purchased the ${selectedPackage?.name} package for Grade ${formData.grade}. Please choose a different grade or package.`);
      }

      // Validate all form fields
      if (!formData.grade || !formData.name || !formData.email || !formData.mobile || !formData.address) {
        throw new Error('Please fill in all required fields');
      }

      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        throw new Error('Please enter a valid email address');
      }

      // Validate mobile number (Australian format)
      if (!isValidAustralianMobile(formData.mobile)) {
        throw new Error('Please enter a valid Australian mobile number (e.g., 0412 345 678)');
      }

      // Get user info
      const userInfo = localStorage.getItem('userInfo');
      if (!userInfo) {
        throw new Error('User not logged in');
      }

      const user = JSON.parse(userInfo);
      const userId = user.id;

      if (!userId) {
        throw new Error('User ID not found');
      }

      // Note: Database update is handled by PaymentSuccess.js after Stripe payment
      logger.log('📝 Form data ready for payment processing');
      
    } catch (err) {
      logger.error('❌ Purchase Error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle Stripe payment redirect
  const handleStripePayment = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Validate all form fields before payment
      const errors = {};
      
      if (!formData.grade) {
        errors.grade = 'Please select a grade';
      }
      
      if (!formData.name || formData.name.trim() === '') {
        errors.name = 'Please enter your name';
      }
      
      if (!formData.email || formData.email.trim() === '') {
        errors.email = 'Please enter your email';
      } else {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email)) {
          errors.email = 'Please enter a valid email address';
        }
      }
      
      // Mobile validation
      if (!formData.mobile || formData.mobile.trim() === '') {
        errors.mobile = 'Please enter your mobile number';
      } else if (!isValidAustralianMobile(formData.mobile)) {
        errors.mobile = 'Please enter a valid Australian mobile number (e.g., 0412 345 678)';
      }
      
      // If there are errors, set them and throw error
      if (Object.keys(errors).length > 0) {
        setFieldErrors(errors);
        throw new Error('Please fill in all required fields before proceeding to payment');
      }
      
      // Clear errors if validation passes
      setFieldErrors({});
      
      // Store form data for post-payment processing
      localStorage.setItem('purchaseFormData', JSON.stringify(formData));
      localStorage.setItem('pendingPackage', JSON.stringify(selectedPackage));
      
      // Get environment-based Stripe URLs
      const stripeUrls = getStripeUrls();
      
      // Get Stripe payment URL based on package
      let stripePaymentUrl = stripeUrls[selectedPackage?.id];
      
      if (!stripePaymentUrl) {
        throw new Error('Package type not recognized or Stripe URL not configured');
      }
      
      // Add customer email to Stripe URL (readonly)
      const customerEmail = formData.email;
      if (customerEmail) {
        const urlSeparator = stripePaymentUrl.includes('?') ? '&' : '?';
        stripePaymentUrl += `${urlSeparator}locked_prefilled_email=${encodeURIComponent(customerEmail)}`;
      }
      
      // Alternative: Use Stripe Checkout with custom URLs (if payment links don't work)
      // You'll need to add Stripe.js library and configure this properly
      // For now, we'll use the payment links as-is
      
      // Debug: Log the actual URL being used
   
      // Open Stripe payment in same page
      window.location.href = stripePaymentUrl;
      
    } catch (err) {
      logger.error('Payment redirect error:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  // Get package icon
  const getPackageIcon = (packageId) => {
    switch(packageId) {
      case 'PKG_PREMIUM': return <Crown className="h-8 w-8 text-yellow-600" />;
      case 'PKG_STANDARD': return <Star className="h-8 w-8 text-purple-600" />;
      default: return <Zap className="h-8 w-8 text-blue-600" />;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <Link 
            to="/packages"
            className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Packages
          </Link>
          <h1 className="text-3xl font-bold text-gray-900">Complete Your Purchase</h1>
        </div>

        {/* Selected Package Summary */}
        {selectedPackage && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-8">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                {getPackageIcon(selectedPackage.id)}
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">{selectedPackage.name}</h2>
                  <p className="text-gray-600">${selectedPackage.price.toFixed(2)}</p>
                </div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-gray-900">
                  ${selectedPackage.price.toFixed(2)}
                </div>
                <p className="text-sm text-amber-600 mt-1">
                  Valid for 1 year from purchase date
                </p>
                {alreadyPurchased && (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 mt-2">
                    <CheckCircle className="h-3 w-3 mr-1" />
                    Already Purchased
                  </span>
                )}
              </div>
            </div>
            
            {alreadyPurchased && (
              <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-amber-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-sm font-medium text-amber-800">Package Already Purchased</h3>
                    <p className="text-sm text-amber-700 mt-1">
                      You have already purchased the {selectedPackage?.name} package for Grade {formData.grade}. You can access your exams from the dashboard or choose a different grade or package to purchase.
                    </p>
                    <div className="mt-3 flex space-x-3">
                      <Link 
                        to="/dashboard"
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-amber-600 hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500"
                      >
                        Go to Dashboard
                      </Link>
                      <Link 
                        to="/packages"
                        className="inline-flex items-center px-4 py-2 border border-amber-300 text-sm font-medium rounded-md text-amber-700 bg-amber-50 hover:bg-amber-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500"
                      >
                        View Other Packages
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-800">{error}</p>
          </div>
        )}

        {/* Purchase Form */}
        <form onSubmit={handlePurchase} className="bg-white rounded-lg shadow-lg p-6">
          {!alreadyPurchased && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              
              {/* Left Column - User Details */}
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <User className="h-5 w-5 mr-2" />
                    Student Information
                  </h3>
                  
                  {/* Grade Selection */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <GraduationCap className="h-4 w-4 inline mr-1" />
                      Grade Level *
                    </label>
                    <select
                      name="grade"
                      value={formData.grade}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                        fieldErrors.grade 
                          ? 'border-red-500 focus:ring-red-500' 
                          : 'border-gray-300 focus:ring-blue-500'
                      }`}
                      required
                    >
                      <option value="">Select Grade</option>
                      <option value="3">Grade 3</option>
                      <option value="5">Grade 5</option>
                      <option value="7">Grade 7</option>
                      <option value="9">Grade 9</option>
                    </select>
                    {fieldErrors.grade && (
                      <p className="mt-1 text-sm text-red-600">{fieldErrors.grade}</p>
                    )}
                  </div>

                  {/* Name */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                        fieldErrors.name 
                          ? 'border-red-500 focus:ring-red-500' 
                          : 'border-gray-300 focus:ring-blue-500'
                      }`}
                      placeholder="Enter full name"
                      required
                    />
                    {fieldErrors.name && (
                      <p className="mt-1 text-sm text-red-600">{fieldErrors.name}</p>
                    )}
                  </div>

                  {/* Email */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Mail className="h-4 w-4 inline mr-1" />
                      Email Address *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                        fieldErrors.email 
                          ? 'border-red-500 focus:ring-red-500' 
                          : 'border-gray-300 focus:ring-blue-500'
                      }`}
                      placeholder="student@example.com"
                      required
                    />
                    {fieldErrors.email && (
                      <p className="mt-1 text-sm text-red-600">{fieldErrors.email}</p>
                    )}
                  </div>

                  {/* Mobile */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Phone className="h-4 w-4 inline mr-1" />
                      Mobile Number *
                    </label>
                    <input
                      type="tel"
                      name="mobile"
                      value={formData.mobile}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                        fieldErrors.mobile 
                          ? 'border-red-500 focus:ring-red-500' 
                          : 'border-gray-300 focus:ring-blue-500'
                      }`}
                      placeholder="+61XXXXXXXXX"
                      required
                    />
                    {fieldErrors.mobile && (
                      <p className="mt-1 text-sm text-red-600">{fieldErrors.mobile}</p>
                    )}
                  </div>

                  {/* Address */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <MapPin className="h-4 w-4 inline mr-1" />
                      Address *
                    </label>
                    <textarea
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      rows="3"
                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                        fieldErrors.address 
                          ? 'border-red-500 focus:ring-red-500' 
                          : 'border-gray-300 focus:ring-blue-500'
                      }`}
                      placeholder="Enter full address"
                      required
                    />
                    {fieldErrors.address && (
                      <p className="mt-1 text-sm text-red-600">{fieldErrors.address}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Right Column - Payment Action */}
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <CreditCard className="h-5 w-5 mr-2" />
                    Complete Your Purchase
                  </h3>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                    <p className="text-blue-800 text-sm">
                      You will be redirected to our secure payment partner to complete your purchase.
                    </p>
                  </div>

                  {/* Terms and Conditions Checkbox */}
                  <div className="mb-4">
                    <label className="flex items-start">
                      <input
                        type="checkbox"
                        checked={termsAccepted}
                        onChange={(e) => setTermsAccepted(e.target.checked)}
                        className="mt-1 mr-2 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-600">
                        I agree to the <Link to="/policies" className="text-blue-600 hover:underline" target="_blank">Terms and Conditions</Link>
                      </span>
                    </label>
                  </div>

                  {/* Stripe Payment Button */}
                  <button
                    type="button"
                    onClick={handleStripePayment}
                    disabled={loading || !termsAccepted}
                    className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors duration-200"
                  >
                    {loading ? (
                      <span className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                        Redirecting to Payment...
                      </span>
                    ) : (
                      `Pay $${selectedPackage?.price.toFixed(2) || '0.00'} with Stripe`
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default Purchase;
